/**
 * Вложения чата: медиа, файлы, голосовые, ссылки.
 *
 * ── Зачем ───────────────────────────────────────────────────────────────────
 *
 * Рабочая переписка — это документы, скриншоты и голосовые за месяцы. Найти
 * среди них нужный прокруткой ленты нельзя: между двумя фотографиями лежат
 * сотни реплик. В Telegram для этого есть отдельный раздел, и он там не
 * украшение — им пользуются постоянно.
 *
 * ── Группировка по месяцам ──────────────────────────────────────────────────
 *
 * Не по дням: в чате, где фотографии приходят раз в неделю, заголовок над
 * каждой съедал бы больше места, чем сами вложения. Месяц — та единица, в
 * которой человек и вспоминает («это было где-то в мае»).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { TelegramMessageDto } from "@tw/shared-types";
import type { MediaSection } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { MediaViewer } from "./MediaViewer.tsx";
import { FileIcon, ImageIcon, MicIcon, VideoIcon } from "./icons.tsx";
import { cn } from "@/lib/utils";
import { telegramIdOf } from "@/lib/message-id.ts";

interface ChatMediaProps {
  chatId: string;
}

const SECTIONS: ReadonlyArray<{ key: MediaSection; label: string }> = [
  { key: "media", label: "Медиа" },
  { key: "files", label: "Файлы" },
  { key: "voice", label: "Голос" },
  { key: "links", label: "Ссылки" },
];

/** Сколько подгружать за раз. */
const PAGE = 40;

const EMPTY: Record<MediaSection, string> = {
  media: "В этом чате нет фотографий и видео",
  files: "В этом чате нет файлов",
  voice: "В этом чате нет голосовых и видеосообщений",
  links: "В этом чате нет ссылок",
};

export function ChatMedia({ chatId }: ChatMediaProps) {
  const { api } = useSession();

  const [section, setSection] = useState<MediaSection>("media");
  const [items, setItems] = useState<TelegramMessageDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<TelegramMessageDto | null>(null);

  /*
   * Номер запроса.
   *
   * Переключение вкладок быстрее ответа сети — обычное дело, и без этого
   * счётчика ответ на «Файлы» дорисовывался бы поверх уже открытых «Медиа».
   * Отменять запрос нечем: он уже ушёл, и вопрос только в том, применять ли
   * его результат.
   */
  const attempt = useRef(0);

  const load = useCallback(
    async (from: string, replace: boolean): Promise<void> => {
      const mine = ++attempt.current;
      setLoading(true);
      setError(null);

      try {
        const { items: page } = await api.chatMediaList(chatId, {
          section,
          limit: PAGE,
          ...(from === "0" ? {} : { from }),
        });

        if (mine !== attempt.current) return;

        setItems((prev) => (replace ? page : [...prev, ...page]));
        // Короткая страница означает конец: Telegram отдал всё, что нашёл.
        setDone(page.length < PAGE);
      } catch (err) {
        if (mine !== attempt.current) return;
        setError(
          err instanceof Error ? err.message : "Не удалось загрузить вложения",
        );
        setDone(true);
      } finally {
        if (mine === attempt.current) setLoading(false);
      }
    },
    [api, chatId, section],
  );

  useEffect(() => {
    setItems([]);
    setDone(false);
    void load("0", true);
  }, [load]);

  const more = (): void => {
    const last = items[items.length - 1];
    if (!last || loading || done) return;
    void load(telegramIdOf(last) ?? last.id, false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        {SECTIONS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setSection(tab.key)}
            className={cn(
              "flex-1 rounded-full border-transparent px-2 py-1 text-[11px] font-medium transition-colors",
              section === tab.key
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <p role="alert" className="text-[11px] text-destructive">
          {error}
        </p>
      )}

      {!loading && items.length === 0 && !error && (
        <p className="py-3 text-center text-[11px] text-muted-foreground">
          {EMPTY[section]}
        </p>
      )}

      {groupByMonth(items).map((group) => (
        <div key={group.key} className="flex flex-col gap-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {group.title}
          </p>

          {section === "media" ? (
            <div className="grid grid-cols-3 gap-1">
              {group.items.map((message) => (
                <Thumb
                  key={message.id}
                  message={message}
                  chatId={chatId}
                  onOpen={() => setViewing(message)}
                />
              ))}
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {group.items.map((message) => (
                <Row
                  key={message.id}
                  message={message}
                  section={section}
                  chatId={chatId}
                />
              ))}
            </ul>
          )}
        </div>
      ))}

      {loading && (
        <p className="py-2 text-center text-[11px] text-muted-foreground">
          Загрузка…
        </p>
      )}

      {!loading && !done && items.length > 0 && (
        <button
          type="button"
          onClick={more}
          className="rounded-full border border-border bg-transparent py-1 text-[11px] font-medium text-brand transition-colors hover:bg-secondary"
        >
          Показать ещё
        </button>
      )}

      {viewing?.media && (
        <MediaViewer
          media={viewing.media}
          src={`/api/chats/${chatId}/messages/${encodeURIComponent(telegramIdOf(viewing) ?? viewing.id)}/media`}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function Thumb({
  message,
  chatId,
  onOpen,
}: {
  message: TelegramMessageDto;
  chatId: string;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const src = `/api/chats/${chatId}/messages/${encodeURIComponent(telegramIdOf(message) ?? message.id)}/media`;
  const video = message.media?.kind === "video";

  return (
    <button
      type="button"
      onClick={onOpen}
      title={formatDate(message.sentAt)}
      aria-label={`Открыть вложение от ${formatDate(message.sentAt)}`}
      className="relative aspect-square overflow-hidden rounded-lg border-transparent bg-secondary p-0"
    >
      {failed ? (
        <span className="grid h-full place-items-center text-muted-foreground">
          <ImageIcon className="size-4" />
        </span>
      ) : (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      )}
      {video && (
        <span className="absolute bottom-0.5 right-0.5 grid size-4 place-items-center rounded-full bg-black/60 text-white">
          <VideoIcon className="size-2.5" />
        </span>
      )}
    </button>
  );
}

function Row({
  message,
  section,
  chatId,
}: {
  message: TelegramMessageDto;
  section: MediaSection;
  chatId: string;
}) {
  if (section === "links") {
    /*
     * Ссылки берём из текста, а не из вложения: сообщение со ссылкой — это
     * обычный текст, у него нет media вовсе. Первая ссылка на сообщение и
     * есть то, ради чего его сюда положили.
     */
    const url = firstUrl(message.text ?? "");
    return (
      <li className="rounded-lg px-2 py-1.5 hover:bg-accent">
        <p className="truncate text-[12px] text-brand">{url ?? message.text}</p>
        <p className="text-[10px] text-muted-foreground">
          {formatDate(message.sentAt)}
        </p>
      </li>
    );
  }

  const voice = section === "voice";
  const name =
    message.media?.fileName ?? (voice ? "Голосовое сообщение" : "Файл");

  return (
    <li className="flex items-center gap-2 rounded-lg px-1 py-1 hover:bg-accent">
      <span
        aria-hidden
        className="grid size-8 flex-none place-items-center rounded-lg bg-secondary text-brand"
      >
        {voice ? (
          <MicIcon className="size-4" />
        ) : (
          <FileIcon className="size-4" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <a
          href={`/api/chats/${chatId}/messages/${encodeURIComponent(telegramIdOf(message) ?? message.id)}/media`}
          className="block truncate text-[12px] text-foreground hover:text-brand"
        >
          {name}
        </a>
        <span className="block text-[10px] text-muted-foreground">
          {[formatDate(message.sentAt), formatSize(message.media?.size ?? null)]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
    </li>
  );
}

interface MonthGroup {
  key: string;
  title: string;
  items: TelegramMessageDto[];
}

/**
 * Разбивка по месяцам с сохранением порядка.
 *
 * Порядок не пересортировывается: Telegram уже отдал от свежего к старому, а
 * повторная сортировка по дате сломала бы вложения без даты — они уехали бы в
 * конец списка вместо своего места.
 */
function groupByMonth(items: readonly TelegramMessageDto[]): MonthGroup[] {
  const groups: MonthGroup[] = [];

  for (const message of items) {
    const date = message.sentAt ? new Date(message.sentAt) : null;
    const key = date ? `${date.getFullYear()}-${date.getMonth()}` : "unknown";
    const title = date
      ? capitalize(
          date.toLocaleDateString("ru-RU", { month: "long", year: "numeric" }),
        )
      : "Без даты";

    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(message);
    else groups.push({ key, title, items: [message] });
  }

  return groups;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return "";
  const units = ["Б", "КБ", "МБ", "ГБ"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

const URL_RE = /https?:\/\/\S+|(?:^|\s)(?:www\.)?[a-z0-9-]+\.[a-z]{2,}\S*/i;

function firstUrl(text: string): string | null {
  const match = URL_RE.exec(text);
  return match ? match[0].trim() : null;
}
